import { InventoryType, OrbitCameras } from '@scaleaq/sitecontrol-lib';

import type { InventoryServiceDefinition } from '@/types.ts';

import { PageNames } from './pageNames.ts';

/** A site service plus the page it is reached through, which supplies its label, icon and nav gate. */
type SiteInventoryService = InventoryServiceDefinition & {
  readonly pageName: PageNames;
  readonly showOnSiteCards?: false;
  /** Set to false when the page reads nothing from the site itself, so an unreachable site still
   * loads it. Omit it for pages backed by the site's own APIs, which need the site reachable. */
  readonly requiresSiteConnection?: false;
};

/**
 * How each site service is detected in inventory. The sidebar uses an entry in both modes; the
 * page gate (`PortableServiceGate`) still applies only in portable mode. Adding a service takes an
 * entry here plus that wrapper; nav, picker, badges and copy all follow from the entry.
 *
 * An entry without a gated route is a bug — the nav item appears and stays clickable, but the page
 * then renders against a site that may not support it — so `siteInventoryServices.test.ts` fails the
 * build when the two drift apart.
 *
 * Alphabetical: the key order decides badge order on the site cards.
 */
export const SITE_INVENTORY_SERVICES = {
  bargeControl: {
    types: [
      InventoryType.BilgePump,
      InventoryType.BargeControlTank,
      InventoryType.BargeControlSilo,
      InventoryType.Genset,
      InventoryType.GioDevice,
    ],
    pageName: PageNames.BargeControl,
  },
  // The generated classification the camera stream widget also gates on, so both agree on what a
  // camera is. Separate enum from `InventoryType` with matching string values, hence the cast.
  // Includes Orbit One, so those sites offer both services.
  cameraControl: {
    types: Object.values(OrbitCameras) as unknown as InventoryType[],
    pageName: PageNames.CameraCheck,
    showOnSiteCards: false,
  },
  hybridPower: { types: [InventoryType.HybridPower], pageName: PageNames.HybridPower },
  // Every reading on this page comes from the cloud measurement and gallery APIs, so it is just as
  // complete while the site itself is unreachable.
  orbitOne: {
    types: [InventoryType.OrbitOne],
    pageName: PageNames.OrbitOne,
    requiresSiteConnection: false,
  },
  penViewer: { types: [InventoryType.Pen], pageName: PageNames.PenViewer },
  upCycle: { types: [InventoryType.DeadFishPlc], pageName: PageNames.UpCycle },
} as const satisfies Record<string, SiteInventoryService>;

/** Name of a service whose availability is decided by cross-site inventory. */
export type SiteServiceName = keyof typeof SITE_INVENTORY_SERVICES;

/** Every registered service; grows on its own as {@link SITE_INVENTORY_SERVICES} does. */
export const ALL_SITE_SERVICES = Object.keys(SITE_INVENTORY_SERVICES) as readonly SiteServiceName[];

const SITE_SERVICE_BY_PAGE_NAME = Object.fromEntries(
  ALL_SITE_SERVICES.map(name => [SITE_INVENTORY_SERVICES[name].pageName, name])
) as Partial<Record<PageNames, SiteServiceName>>;

/** The service reached through a page, or `undefined` when that page is not inventory-gated. */
export const siteServiceForPage = (pageName: string): SiteServiceName | undefined =>
  SITE_SERVICE_BY_PAGE_NAME[pageName as PageNames];

/** Whether an unreachable site keeps the service's page from loading, and so is worth warning about.
 * The flag is only ever set to false, so its presence is the whole answer. */
export const serviceRequiresSiteConnection = (service: SiteServiceName): boolean =>
  !('requiresSiteConnection' in SITE_INVENTORY_SERVICES[service]);
